import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { FormattedMessage } from 'react-intl';
import get from 'lodash/get';
import pick from 'lodash/pick';
import assign from 'lodash/assign';
import set from 'lodash/set';
import format from 'date-fns/format';
import Form from '../../components/Form';
import Divider from '../../components/Divider';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Label from '../../components/Label';
import Header from '../../components/Header';
import Icon from '../../components/Icon';
import Message from '../../components/Message';
import Segment from '../../components/Segment';
import Dimmer from '../../components/Dimmer';
import Loader from '../../components/Loader';
import Toast from '../../components/Toast';
import Radio from '../../components/Radio';
import messages from './messages';
import { isLastTransferFailure } from '../SFTPSettingsPage/sftp-settings';
import DeleteConnectionModal from './delete-connection-modal';
import './styles.css';

const newSFTPLink = '/settings/sftp-settings/sftp-list';

export const UnExpectedErrorComponent = () => (
  <Message icon error>
    <Icon name="remove circle" size="small" />
    <Message.Content data-cy="new-sftp-connection-message-heading">
      <Message.Header>
        <FormattedMessage id="newsftpconnection-MessageHeading" defaultMessage="Something went wrong" />
      </Message.Header>
      <p>
        <FormattedMessage {...messages.unexpectedErrorMessageContent} />
      </p>
    </Message.Content>
  </Message>
);

export const TestConnectionFailureMessage = (props) => (
  <Message icon warning>
    <Icon name="exclamation circle" size="small" />
    <Message.Content data-cy="new-sftp-connection-failure-message">
      <Message.Header>
        <FormattedMessage {...messages.testConnectionFailureHeading} />
      </Message.Header>
      <p>{props.failureMessage}</p>
    </Message.Content>
  </Message>
);

export const LastTransferFailureMessage = () => (
  <Message icon warning>
    <Icon name="exclamation circle" size="small" />
    <Message.Content>
      <Message.Header>
        <FormattedMessage {...messages.transferFailureHeading} />
      </Message.Header>
      <p><FormattedMessage {...messages.genericTransferFailureMessageContent} /></p>
    </Message.Content>
  </Message>
);

class SaveSFTPConnection extends React.PureComponent {
  static propTypes = {
    mutateNewSftpConnection: PropTypes.func,
    mutateSshKeyGen: PropTypes.func,
    viewer: PropTypes.object,
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    mutateDeleteSftpConnection: PropTypes.func,
  };

  constructor(props) {
    super(props);
    const amExportConfig = this.props.viewer.activeMatchExportConfiguration.isNew? null : this.props.viewer.activeMatchExportConfiguration;
    this.state = {
      authenticationType: amExportConfig ? get(amExportConfig, 'authenticationType'): 'password',
      hostValidationError: false,
      portValidationError: false,
      nameValidationError: false,
      passwordValidationError: false,
      checkboxValidationError: false,
      sshValidationError: false,
      showTestConnectionFailure: false,
      testConnectionFailureMessage: amExportConfig ? get(amExportConfig, 'connectionTestError.message'): '',
      error: false,
      sftpHost: amExportConfig ? get(amExportConfig, 'sftpHost'): '',
      sftpPort: amExportConfig ? get(amExportConfig, 'sftpPort'): 22,
      sftpUsername: amExportConfig ? get(amExportConfig, 'sftpUsername'): '',
      sftpPath: amExportConfig ? get(amExportConfig, 'sftpPath'): '',
      sftpPassword: amExportConfig ? get(amExportConfig, 'sftpPassword'): '',
      schedule: amExportConfig ? get(amExportConfig, 'schedule'): ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      loading: false,
      publicKey: amExportConfig ? get(amExportConfig, 'publicKey'): '',
      newsshKey: false,
      openModal: false,
      mutationFailure: false,
    };
  }

  onSaveConnection = (event) => {
    event.preventDefault();

    const newState = { };
    let isReadyToSave = true;
    if (this.state.sftpHost === '') {
      isReadyToSave = false;
      newState.hostValidationError = true;
      newState.error = true;
      newState.mutationFailure= false;
      this.focusMessage();
    }
    if ((Number.isNaN(this.state.sftpPort)) || (this.state.sftpPort < 1 || this.state.sftpPort > 65535)) {
      isReadyToSave = false;
      newState.portValidationError = true;
      newState.error = true;
      newState.mutationFailure= false;
      this.focusMessage();
    }
    if (this.state.sftpUsername === '') {
      isReadyToSave = false;
      newState.nameValidationError = true;
      newState.error = true;
      newState.mutationFailure= false;
      this.focusMessage();
    }
    if (this.state.authenticationType === 'password' && this.state.sftpPassword === '') {
      isReadyToSave = false;
      newState.passwordValidationError = true;
      newState.error = true;
      newState.mutationFailure= false;
      this.focusMessage();
    }
    if (this.state.authenticationType === 'sshkey' && this.state.publicKey === '') {
      isReadyToSave = false;
      newState.sshValidationError = true;
      newState.error = true;
      newState.mutationFailure= false;
      this.focusMessage();
    }
    if (this.state.schedule.length === 0) {
      isReadyToSave = false;
      newState.checkboxValidationError = true;
      newState.error = true;
      newState.mutationFailure= false;
      this.focusMessage();
    }

    const amExportConfig = this.props.viewer.activeMatchExportConfiguration;
    const mutationBody = assign({},
      { id: get(amExportConfig, 'id') },
      { ...pick(this.state,
        ['sftpHost',
          'sftpPath',
          'sftpUsername',
          'sftpPassword',
          'authenticationType',
          'schedule']) });
    // ensure sftpPort is a number
    set(mutationBody, 'sftpPort', parseInt(get(this.state, 'sftpPort'), 10));

    const onSuccess = (data) => {
      const testConnection = data.data.saveAMExportConfig.amExportConfig.connectionTestSuccess;
      /* istanbul ignore if */
      if (testConnection) {
        this.successToastPageRedirect();
      } else {
        this.setState({
          error: false,
          loading: false,
          showTestConnectionFailure: true,
          testConnectionFailureMessage: data.data.saveAMExportConfig.amExportConfig.connectionTestError.message,
          toastMessage: this.failureNotification(),
          mutationFailure: false,
        });
      }
      this.focusMessage();
    };

    const onFailure = () => {
      this.setState({
        error: false,
        loading: false,
        mutationFailure: true,
      });
      this.focusMessage();
    };

    this.setState(newState);
    if (isReadyToSave) {
      this.setState({
        error: false,
        loading: true,
        toastMessage: '',
        mutationFailure: false,
      });
      const connectionLoader = document.getElementById('username');
      /* istanbul ignore if */
      if (connectionLoader) { connectionLoader.scrollIntoView(); }
      this.props.mutateNewSftpConnection(set({}, 'variables.input.amExportConfig', mutationBody)).then(onSuccess, onFailure);
    }
  };

  onGenerateSSHKey = () => {
    const onSuccess = (data) => {
      const sshKeyGenerated = data.data.generateSFTPKeypair.publicKey;
      this.setState({ sshValidationError: false, newsshKey: true, publicKey: sshKeyGenerated, mutationFailure: false });
    };
    const onFailure = () => {
      this.setState({ mutationFailure: true });
      this.focusMessage();
    };
    const inputVal = {
      variables: {
        input: {
          username: this.state.sftpUsername,
        },
      },
    };
    this.props.mutateSshKeyGen(inputVal).then(onSuccess, onFailure);
  };

  deleteConnection = () => {
    const onSuccess = () => {
      this.deleteToastPageRedirect();
    };
    const onFailure = () => {
      this.setState({ mutationFailure: true });
    };
    this.props.mutateDeleteSftpConnection().then(onSuccess, onFailure);
  };

  failureNotification = () =>
    (
      <Toast
        active
        title=""
        kind="warning"
        onDeactivate={() => this.setState({ toastMessage: '' })}
        description="Configuration saved with errors"
      />
    );

  focusMessage = () => {
    const message = document.getElementById('newsftpform');
    /* istanbul ignore if */
    if (message) { message.scrollIntoView(); }
  };

  successToastPageRedirect = () => {
    this.props.router.push({
      pathname: '/settings/sftp-settings/sftp-list',
      state: {
        success: true,
      },
    });
  };

  deleteToastPageRedirect = () => {
    this.props.router.push({
      pathname: '/settings/sftp-settings/sftp-list',
      state: {
        delete: true,
      },
    });
  };

  handlePasswordAuthentication = () => {
    this.setState({ authenticationType: 'password' });
  };

  handleSshAuthentication = () => {
    this.setState({ authenticationType: 'sshkey', passwordValidationError: false });
  };

  changeHostValue = (e) => {
    this.setState({ sftpHost: e.target.value, hostValidationError: false });
  };

  changePortValue = (e) => {
    this.setState({ sftpPort: e.target.value, portValidationError: false });
  };

  changeUserNameValue = (e) => {
    this.setState({ sftpUsername: e.target.value, nameValidationError: false });
  };

  changePathNameValue = (e) => {
    this.setState({ sftpPath: e.target.value });
  };

  changePasswordValue = (e) => {
    this.setState({ sftpPassword: e.target.value, passwordValidationError: false });
  };

  selectedDay = (day) => {
    const index = this.state.schedule.indexOf(day);
    if (index < 0) {
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ schedule: [...this.state.schedule, day], checkboxValidationError: false });
    } else {
      this.state.schedule.splice(index, 1);
      // eslint-disable-next-line react/no-access-state-in-setstate
      this.setState({ schedule: [...this.state.schedule], checkboxValidationError: false });
    }
  };

  closeModal = () => {
    this.setState({ openModal: false });
  };

  showModal = () => {
    this.setState({ openModal: true });
  };

  render() {
    const { authenticationType, sftpHost, sftpPort, sftpUsername, sftpPath, toastMessage,
      hostValidationError, portValidationError, nameValidationError,
      passwordValidationError, sshValidationError,
      publicKey, newsshKey, testConnectionFailureMessage } = this.state;

    const amExportConfig = this.props.viewer.activeMatchExportConfiguration;
    const { connectionTestSuccess, lastModified } = amExportConfig;
    const { activeMatchExportTransferSummary } = this.props.viewer;
    const successfulJob = activeMatchExportTransferSummary ? get(activeMatchExportTransferSummary, 'successfulJob'): null;
    const transferCreatedAt = activeMatchExportTransferSummary ? get(activeMatchExportTransferSummary, 'createdAt'): null;
    const isNew = get(amExportConfig, 'isNew', false);
    const ipLists = amExportConfig && get(amExportConfig, 'whitelistIPs') && get(amExportConfig, 'whitelistIPs').split(',').join('\n');
    const showTestConnectionFailureMessage = (!this.state.error && this.state.showTestConnectionFailure) ||
      (!isNew && !connectionTestSuccess && !this.state.showTestConnectionFailure);
    let showTransferFailedMessage = false;
    if (!showTestConnectionFailureMessage) {
      showTransferFailedMessage = !isNew && !this.state.error && activeMatchExportTransferSummary &&
        isLastTransferFailure(transferCreatedAt, successfulJob, lastModified);
    }

    return (
      <div styleName="newsftpform" id="newsftpform">
        { toastMessage }
        <Header as="h2" styleName="pageTitle">
          <Link
            styleName="titileLink"
            to={newSFTPLink}
          >SFTP Data Transfer
          </Link>{isNew ? '/ Set Up Connection' : '/ Edit Connection'}
        </Header>
        <Segment styleName="formSegment">
          <Dimmer inverted styleName="formDimmer" active={this.state.loading}>
            <Loader size="large"><p id="connectionloader" styleName="testingConnection">Testing Connection</p></Loader>
          </Dimmer>
          {
            !isNew && get(amExportConfig, 'lastModified') ? (
              <p styleName="lastModified">
                Last updated {format(get(amExportConfig, 'lastModified'), 'MMM dd, yyyy')} by {get(amExportConfig, 'modifiedBy.name')}
              </p>
            ): null
          }
          {this.state.error && <UnExpectedErrorComponent />}
          {showTestConnectionFailureMessage && <TestConnectionFailureMessage failureMessage={testConnectionFailureMessage} />}
          {showTransferFailedMessage && <LastTransferFailureMessage />}
          <Form id="newSftpConnection">
            <Header as="h3" styleName="pageHeading"><FormattedMessage {...messages.connectionHeading} /></Header>
            <p styleName="pageSubHeading"><FormattedMessage {...messages.connectionSubHeading} /></p>
            <div styleName="bottom-row">
              <div styleName="bottom-col">
                <Form.Input
                  type="text"
                  label="Host"
                  value={sftpHost}
                  autoComplete="off"
                  styleName={hostValidationError?'hostError':'host'}
                  onChange={this.changeHostValue}
                  id="sftpHost"
                />
                { hostValidationError && (
                <Label role="tooltip" basic styleName="validationError" pointing>
                  <Icon name="exclamation circle" styleName="validationError" size="large" />
                  <FormattedMessage {...messages.hostValidation} />
                </Label>
                )}
              </div>
              <div styleName="bottom-col">
                <Form.Input
                  type="text"
                  maxLength={5}
                  autoComplete="off"
                  label="Port"
                  styleName={portValidationError?'portError':'port'}
                  value={sftpPort}
                  onChange={this.changePortValue}
                  id="sftpPort"
                />
                { portValidationError && (
                <Label role="tooltip" basic styleName="portTooltipError" className="pointingArrow" data-cy="new-sftp-connection-port-tooltip-error" pointing>
                  <Icon name="exclamation circle" styleName="validationError" size="large" />
                  <FormattedMessage {...messages.portValidation} />
                </Label>
                )}
              </div>
            </div>
            <Form.Group>
              <Form.Input
                type="text"
                label="Path (optional)"
                styleName="host"
                id="sftpPath"
                value={sftpPath}
                onChange={this.changePathNameValue}
              />
            </Form.Group>
            <Divider styleName="divider" />
            <Header as="h3" styleName="pageHeading" id="connectionCredentials">
              <FormattedMessage {...messages.connectionCredentialsHeading} />
            </Header>
            <p styleName="pageSubHeading"><FormattedMessage {...messages.connectionCredentialsSubHeading} /></p>
            <Form.Group>
              <Form.Input
                label="Username"
                id="username"
                type="text"
                autoComplete="off"
                value={sftpUsername}
                onChange={this.changeUserNameValue}
                styleName={nameValidationError?'hostError':'host'}
              />
            </Form.Group>
            {nameValidationError && (
            <Label role="tooltip" basic styleName="validationError" pointing>
              <Icon name="exclamation circle" styleName="validationError" size="large" />
              <FormattedMessage {...messages.nameValidation} />
            </Label>
            )}
            <div styleName="authenticationHeaderTop">
              <label htmlFor="radio" styleName="authentication">Authentication Method</label>
              <div styleName="radioButtons">
                <Form.Group inline>
                  <Radio
                    id="password"
                    value="password"
                    checked={authenticationType === 'password'}
                    onChange={this.handlePasswordAuthentication}
                  />
                  <label htmlFor="password" styleName="radioPassword">Password</label>
                  <Radio
                    id="sshkey"
                    value="sshkey"
                    checked={authenticationType === 'sshkey'}
                    onChange={this.handleSshAuthentication}
                  />
                  <label htmlFor="sshkey" styleName="radioPassword">SSH Public Key</label>
                </Form.Group>
              </div>
            </div>
            {authenticationType === 'password'? (
              <div>
                <Form.Group>
                  <Form.Input
                    type="password"
                    value={this.state.sftpPassword}
                    autoComplete="off"
                    styleName={passwordValidationError?'hostError':'host'}
                    id="sftpPasswordText"
                    onChange={this.changePasswordValue}
                  />
                </Form.Group>
                { passwordValidationError && (
                <Label role="tooltip" basic styleName="validationError" pointing>
                  <Icon name="exclamation circle" styleName="validationError" size="large" />
                  <FormattedMessage {...messages.passwordValidation} />
                </Label>
                )}
              </div>
            ): (
              <div>
                {!publicKey ? (
                  <Button
                    styleName="button"
                    id="sftpGenerateKey"
                    onClick={this.onGenerateSSHKey}
                  >Generate Key
                  </Button>
                ): (
                  <div>
                    {newsshKey?(
                      <p styleName="ssh-key-copy-text">
                        <FormattedMessage {...messages.connectionSSHGenerateKey} />
                      </p>
                    ): null}
                    <Message info styleName="ssh-rectangle">
                      <Message.Content>
                        <Message.Header styleName="ssh-key-header">
                          <div>
                            <Icon name="key" size="small" styleName="ssh-key-icon" />
                            {get(amExportConfig, 'sshKeyGeneratedAt') && (
                            <p>
                              Using public key generated {format(get(amExportConfig, 'sshKeyGeneratedAt'), 'MMM dd, yyyy')}
                              &nbsp;by {get(amExportConfig, 'sshKeyGeneratedBy.name')}
                            </p>
                            )}
                          </div>
                        </Message.Header>
                        {newsshKey? (
                          <Message.Content className="ssh-key-scroll" styleName="ssh-key-rectangle">
                            <p styleName="ssh-key-text">{publicKey}</p>
                          </Message.Content>
                        ): null}
                      </Message.Content>
                    </Message>
                    <Button
                      styleName="button"
                      id="sftpReGenerateKey"
                      onClick={this.onGenerateSSHKey}
                    >Delete &amp; Regenerate Key
                    </Button>
                  </div>
                )}
                <div styleName="sshhint">
                  <p><FormattedMessage {...messages.connectionCredentialsSshHint} /></p>
                </div>
                { sshValidationError && (
                <Label role="tooltip" basic styleName="validationError" pointing>
                  <Icon name="exclamation circle" styleName="validationError" size="large" />
                  <FormattedMessage {...messages.sshValidation} />
                </Label>
                )}
              </div>
            )}
            <Divider styleName="divider" />
            <Header as="h3" styleName="pageHeading" id="transferFrequency">
              <FormattedMessage {...messages.connectionTransferFrequencyHeading} />
            </Header>
            <p styleName="pageSubHeading"><FormattedMessage {...messages.connectionTransferFrequencySubHeading} /></p>
            <Form.Group inline>
              <Checkbox
                label={{ htmlFor: 'sftpSun', children: 'Sun' }}
                styleName={this.state.checkboxValidationError?'checkboxError':''}
                checked={this.state.schedule.includes('Sunday')}
                name="sun"
                id="sftpSun"
                onClick={() => this.selectedDay('Sunday')}
              />
              <span styleName="checkbox">
                <Checkbox
                  label={{ htmlFor: 'sftpMon', children: 'Mon' }}
                  styleName={this.state.checkboxValidationError?'checkboxError':''}
                  name="mon"
                  id="sftpMon"
                  checked={this.state.schedule.includes('Monday')}
                  onClick={() => this.selectedDay('Monday')}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  label={{ htmlFor: 'sftpTue', children: 'Tue' }}
                  styleName={this.state.checkboxValidationError?'checkboxError':''}
                  name="tue"
                  id="sftpTue"
                  checked={this.state.schedule.includes('Tuesday')}
                  onClick={() => this.selectedDay('Tuesday')}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  label={{ htmlFor: 'sftpWed', children: 'Wed' }}
                  styleName={this.state.checkboxValidationError?'checkboxError':''}
                  name="wed"
                  id="sftpWed"
                  checked={this.state.schedule.includes('Wednesday')}
                  onClick={() => this.selectedDay('Wednesday')}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  label={{ htmlFor: 'sftpThu', children: 'Thu' }}
                  styleName={this.state.checkboxValidationError?'checkboxError':''}
                  name="thu"
                  id="sftpThu"
                  checked={this.state.schedule.includes('Thursday')}
                  onClick={() => this.selectedDay('Thursday')}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  label={{ htmlFor: 'sftpFri', children: 'Fri' }}
                  styleName={this.state.checkboxValidationError?'checkboxError':''}
                  name="fri"
                  id="sftpFri"
                  checked={this.state.schedule.includes('Friday')}
                  onClick={() => this.selectedDay('Friday')}
                />
              </span>
              <span styleName="checkbox">
                <Checkbox
                  label={{ htmlFor: 'sftpSat', children: 'Sat' }}
                  styleName={this.state.checkboxValidationError?'checkboxError':''}
                  checked={this.state.schedule.includes('Saturday')}
                  name="sat"
                  id="sftpSat"
                  onClick={() => this.selectedDay('Saturday')}
                />
              </span>
            </Form.Group>
            { this.state.checkboxValidationError && (
            <Label role="tooltip" basic styleName="validationError" pointing>
              <Icon name="exclamation circle" styleName="validationError" size="large" />
              <FormattedMessage {...messages.checkboxValidation} />
            </Label>
            )}
            <div styleName="transferHint">
              <p><FormattedMessage {...messages.connectionTransferFrequencyHint} /></p>
            </div>
            <Divider styleName="divider" />
            <Header as="h3" styleName="pageHeading"><FormattedMessage {...messages.connectionIpWhitelistingHeading} /></Header>
            <p styleName="pageSubHeading"><FormattedMessage {...messages.connectionIpWhitelistingSubHeading} /></p>
            <div styleName="ipAddresses">
              <p styleName="ipNumbers">{ipLists}</p>
            </div>
            <Divider styleName="divider" />
          </Form>
          {
            !isNew ? (
              <Link href="#" onClick={this.showModal} styleName="trashIcon" data-cy="edit-sftp-connection-delete-config-button"> {/* eslint-disable-line jsx-a11y/anchor-is-valid */}
                <Icon name="trash alternate" />Delete this configuration
              </Link>
            ):null
          }

          <div styleName="formCompletion">
            <Button
              styleName="save"
              id="sftpSave"
              type="submit"
              onClick={this.onSaveConnection}
            >TEST AND SAVE
            </Button>
          </div>
        </Segment>
        <DeleteConnectionModal
          open={this.state.openModal}
          onClose={this.closeModal}
          deleteConnection={this.deleteConnection}
        />
      </div>
    );
  }
}

export default SaveSFTPConnection;

TestConnectionFailureMessage.propTypes = {
  failureMessage: PropTypes.string.isRequired,
};
